import { useSelector } from 'react-redux';
import caseActions from 'redux/actions/caseActions';
import caseCreationActions from 'redux/actions/caseCreationActions';
import { contactUsStart } from 'redux/actions/contactActions';
import {
  caseCreationRetryAttemptsSelector,
  decisionTreeDropOffContentSelector,
} from 'redux/selectors/caseCreation';
import {
  caseCreationMaxRetriesSelector,
  nextStepsCaseCreationSelector,
  showCaseReferenceSelector,
  buttonBehaviourIfDropOffSelector,
  buttonBehaviourIfErrorSelector,
  buttonBehaviourIfSuccessSelector,
  buttonBehaviourIfExcludedSelector,
} from 'redux/selectors/config';
import routes from 'Routes/routesConfig';
const { retryCreateCaseStart } = caseCreationActions;
const { followMyCaseStart } = caseActions;

export const InformationContentTypes = {
  CASE_CREATED: 'CASE_CREATED',
  CASE_CREATED_ERROR: 'CASE_CREATED_ERROR',
  CASE_CREATED_EXCLUDED: 'CASE_CREATED_EXCLUDED',
  CASE_CREATED_DROPOFF: 'CASE_CREATED_DROPOFF',
  CASE_CREATED_ONGOING: 'CASE_CREATED_ONGOING',
  CASE_CREATED_ONGOING_ICVC: 'CASE_CREATED_DROPOFF_ICVC',
  CASE_CREATED_DROPOFF_SUCCESS: 'CASE_CREATED_DROPOFF_SUCCESS',
};

export const NextStepKeys = {
  SUCCESS: 'success',
  ON_GOING: 'on_going',
  ON_GOING_ICVC: 'on_going_icvc',
};

const ButtonBehaviours = {
  FOLLOW_UP: 'follow_up',
  CONTACT_US: 'contact_us',
  NO_BUTTON: 'no_button',
};

const getButtonBehaviour = (behaviour, category) => {
  switch (behaviour) {
    case ButtonBehaviours.FOLLOW_UP:
      return {
        action: followMyCaseStart,
        text: 'case_creation-follow_my_case-text',
        eventAnalytics: ['follow_my_case', category],
      };
    case ButtonBehaviours.CONTACT_US:
      return {
        action: contactUsStart,
        text: 'case_creation-contact_us-text',
        eventAnalytics: ['contact_us', category],
      };
    case ButtonBehaviours.NO_BUTTON:
    default:
      return {};
  }
};

const getNextStepsKey = type => {
  switch (type) {
    case InformationContentTypes.CASE_CREATED:
      return NextStepKeys.SUCCESS;
    case InformationContentTypes.CASE_CREATED_ONGOING:
      return NextStepKeys.ON_GOING;
    case InformationContentTypes.CASE_CREATED_ONGOING_ICVC:
      return NextStepKeys.ON_GOING_ICVC;
    default:
      return 'default';
  }
};

export const NORMAL_DROP_OFF = 'normal';

export const useInformationContent = (key, showUnavailabilityBanner) => {
  const case_creation_max_retries = useSelector(caseCreationMaxRetriesSelector);
  const retryAttempts = useSelector(caseCreationRetryAttemptsSelector);
  const dropOffContent = useSelector(decisionTreeDropOffContentSelector);
  const numberOfNextSteps = useSelector(
    nextStepsCaseCreationSelector(`next_steps_${key.toLowerCase()}`),
  );
  const showCaseReference = useSelector(showCaseReferenceSelector);
  const buttonBehaviourIfSuccess = useSelector(
    buttonBehaviourIfSuccessSelector,
  );
  const buttonBehaviourIfDropOff = useSelector(
    buttonBehaviourIfDropOffSelector,
  );
  const buttonBehaviourIfExcluded = useSelector(
    buttonBehaviourIfExcludedSelector,
  );
  const buttonBehaviourIfError = useSelector(buttonBehaviourIfErrorSelector);
  const nextStepsType = getNextStepsKey(key);

  switch (key) {
    case InformationContentTypes.CASE_CREATED_ERROR:
      const getErrorButtonRetryProps = {
        text: 'case_creation_retry-text',
        isSecondaryButton: true,
        action: retryCreateCaseStart,
        eventAnalytics: ['retry', 'error'],
      };

      const getErrorButtonContactProps = {
        ...getButtonBehaviour(buttonBehaviourIfError, 'error'),
        isSecondaryButton: true,
      };

      return {
        ...InformationContent[key],
        numberOfNextSteps,
        buttonProps:
          retryAttempts > case_creation_max_retries - 1
            ? getErrorButtonContactProps
            : getErrorButtonRetryProps,
      };
    case InformationContentTypes.CASE_CREATED_EXCLUDED:
      return {
        ...InformationContent[key],
        description: dropOffContent,
        buttonProps: getButtonBehaviour(buttonBehaviourIfExcluded, 'excluded'),
        numberOfNextSteps,
        showCaseReference,
      };
    case InformationContentTypes.CASE_CREATED_DROPOFF:
      return {
        ...InformationContent[key],
        buttonProps: getButtonBehaviour(buttonBehaviourIfDropOff, 'drop_off'),
        numberOfNextSteps,
      };
    case InformationContentTypes.CASE_CREATED:
      return {
        ...InformationContent[key],
        title: !showUnavailabilityBanner
          ? InformationContent[key].title
          : `unavailability_${InformationContent[key].title}`,
        defaultDescription: !showUnavailabilityBanner
          ? InformationContent[key].defaultDescription
          : `unavailability_${InformationContent[key].defaultDescription}`,
        buttonProps: {
          ...getButtonBehaviour(buttonBehaviourIfSuccess, 'confirmation'),
        },
        showCaseReference,
        nextStepsType,
        numberOfNextSteps,
      };
    case InformationContentTypes.CASE_CREATED_ONGOING_ICVC:
    case InformationContentTypes.CASE_CREATED_ONGOING:
      return {
        ...InformationContent[key],
        showCaseReference,
        nextStepsType,
        numberOfNextSteps,
      };
    case InformationContentTypes.CASE_CREATED_DROPOFF_SUCCESS:
      return {
        ...InformationContent[key],
        numberOfNextSteps,
        showCaseReference,
      };
    default:
      return InformationContent[key];
  }
};

const backText = 'case_creation-back-button';
export const InformationContent = {
  [InformationContentTypes.CASE_CREATED]: {
    backText,
    backAnalytics: ['back', 'confirmation'],
    backTo: routes.CASE_OPENING,
    defaultDescription: 'case_creation_success-description',
    image: 'image_success_screen',
    currentPage: 'success',
    title: 'case_creation_success-title',
  },
  [InformationContentTypes.CASE_CREATED_ERROR]: {
    backText,
    backAnalytics: ['back', 'error'],
    defaultDescription: 'case_creation_error-description',
    backTo: routes.CASE_OPENING,
    image: 'image_error_screen',
    currentPage: 'error',
    title: 'case_creation_error-title',
  },
  [InformationContentTypes.CASE_CREATED_EXCLUDED]: {
    backText,
    backAnalytics: ['back', 'error'],
    defaultDescription: 'case_creation_excluded-description',
    backTo: routes.CASE_OPENING,
    image: 'image_not_covered_screen',
    currentPage: 'excluded',
    title: 'case_creation_excluded-title',
  },
  [InformationContentTypes.CASE_CREATED_DROPOFF]: {
    backText,
    backAnalytics: ['back', 'drop_off'],
    backTo: routes.CASE_OPENING,
    defaultDescription: 'drop_off-default_text',
    image: 'image_not_covered_screen',
    currentPage: 'dropoff',
    title: 'drop_off-title',
  },
  [InformationContentTypes.CASE_CREATED_DROPOFF_SUCCESS]: {
    backText,
    backTo: routes.CASE_OPENING,
    backAnalytics: ['back', 'drop_off_success'],
    buttonProps: {},
    CASE_CREATED_DROPOFF_SUCCESS: ['back', 'drop_off_success'],
    defaultDescription: 'case_creation_drop_off_success-description',
    image: 'image_soft_fix_screen',
    currentPage: 'dropoff_success',
    title: 'case_creation_drop_off_success-title',
  },
  [InformationContentTypes.CASE_CREATED_ONGOING]: {
    backText,
    backAnalytics: ['back', 'callback_page'],
    backTo: routes.CASE_OPENING,
    buttonProps: {},
    defaultDescription: 'callback_page-text',
    image: 'image_submitted_screen',
    currentPage: 'callback_page',
    title: 'callback_page-title',
  },
  [InformationContentTypes.CASE_CREATED_ONGOING_ICVC]: {
    backText,
    backAnalytics: ['back', 'callback_page-policy_error'],
    backTo: routes.CASE_OPENING,
    buttonProps: {},
    defaultDescription: 'callback_page-policy_error_text',
    image: 'image_submitted_screen',
    currentPage: 'callback_page-policy_error',
    title: 'callback_page-policy_error_title',
  },
};
